import React, { ChangeEvent, FC, useCallback, useRef, useState, useMemo, useEffect } from 'react'
import { Button, Heading, Icon, Modal, Text, Dropdown, Input, InputBirthday } from '@afterpaytouch/core'
import { useRouter } from 'next/router'
import { SupportedLocale } from '@afterpay/types'
import { isDateInvalid } from '../../../utils'
import { useConsumerSizes } from '../../../../../utils/responsive'
import { Section75DisputeModalSteps, useSection75DisputeModalContext } from '../../Section75DisputeModalContext'
import { MAX_FILE_SIZE } from '../../../../../model'
import { DISPUTE_REASON_OPTIONS } from '../../types'
import { useTranslation } from 'next-i18next'
import styles from './DisputeDetails.module.scss'

const I18N_NAMESPACE = ['common', 'dispute']

export const DisputeDetails: FC = () => {
  const { textSize } = useConsumerSizes()
  const { t } = useTranslation(I18N_NAMESPACE)
  const router = useRouter()
  const locale = router?.locale as SupportedLocale
  const {
    setStep,
    selectedItems,
    setSelectedItems,
    files,
    setFiles,
    order,
    expectedDeliveryDate,
    setExpectedDeliveryDate,
    currentItemIndex,
    setCurrentItemIndex,
    setReason,
  } = useSection75DisputeModalContext()

  const uploadRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string>('')

  const backHandler = useCallback(() => {
    if (currentItemIndex !== null && currentItemIndex > 0) {
      // Go to previous item
      setCurrentItemIndex(currentItemIndex - 1)
    } else {
      // Go back to ChooseItems if we're on the first item
      setCurrentItemIndex(null)
      setStep(Section75DisputeModalSteps.ChooseItems)
    }
  }, [setStep, currentItemIndex, setCurrentItemIndex])

  const handleNext = useCallback(() => {
    if (currentItemIndex !== null && currentItemIndex < selectedItems.length - 1) {
      // Go to next item
      setCurrentItemIndex(currentItemIndex + 1)
    } else {
      // All items completed, go to DisputeAmount
      setCurrentItemIndex(null)
      setStep(Section75DisputeModalSteps.DisputeAmount)
    }
  }, [setStep, currentItemIndex, setCurrentItemIndex, selectedItems.length])

  const addFileButtonClickHandler = useCallback(() => {
    uploadRef.current?.click()
  }, [])

  const fileChangedHandler = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      // @ts-ignore: OPERATION BLEED STOPPER
      const fileList = Array.from(event.target.files)

      const fileOutOfSizeList = fileList.filter((file: File) => file.size > MAX_FILE_SIZE)
      if (fileOutOfSizeList.length > 0) {
        setError(t('submitEvidence:error:exceedSize'))
        return
      }

      // Add files to global files array
      setFiles([...files, ...fileList])

      // Add file names to the current item's fileKeys if we have a current item
      if (currentItemIndex !== null && selectedItems[currentItemIndex]) {
        const updatedItems = [...selectedItems]
        const currentItem = updatedItems[currentItemIndex]
        const newFileNames = fileList.map((file) => file.name)

        updatedItems[currentItemIndex] = {
          ...currentItem,
          fileKeys: [...(currentItem.fileKeys || []), ...newFileNames],
        }

        setSelectedItems(updatedItems)
      }

      setError('')
      event.target.value = ''
    },
    [files, setFiles, currentItemIndex, selectedItems, setSelectedItems]
  )

  const reasonChangeHandler = useCallback(
    (reason) => {
      const trimmedReason = reason.trim()

      // Update the reason in the current selected item
      if (currentItemIndex !== null && selectedItems[currentItemIndex]) {
        const updatedItems = [...selectedItems]
        updatedItems[currentItemIndex] = {
          ...updatedItems[currentItemIndex],
          reason: trimmedReason,
        }
        setSelectedItems(updatedItems)

        // If this is the first item, also set the global reason
        if (currentItemIndex === 0) {
          setReason(trimmedReason)
        }
      }
    },
    [currentItemIndex, selectedItems, setSelectedItems, setReason]
  )

  const expectedDeliveryDateChangeHandler = useCallback(
    (e, date): void => {
      setExpectedDeliveryDate(date ?? null)
    },
    [setExpectedDeliveryDate]
  )

  const expectedDeliveryDateBlurHandler = useCallback(
    (e, date): void => {
      setExpectedDeliveryDate(date ?? null)
    },
    [setExpectedDeliveryDate]
  )

  const noteChangeHandler = useCallback(
    (e) => {
      const description = e.target.value

      // Update the description in the current selected item
      if (currentItemIndex !== null && selectedItems[currentItemIndex]) {
        const updatedItems = [...selectedItems]
        updatedItems[currentItemIndex] = {
          ...updatedItems[currentItemIndex],
          description,
        }
        setSelectedItems(updatedItems)
      }
    },
    [currentItemIndex, selectedItems, setSelectedItems]
  )

  // Get the current item to display (default to first item if no current item set)
  const currentItem = currentItemIndex !== null && selectedItems[currentItemIndex] ? selectedItems[currentItemIndex] : selectedItems[0]
  const selectedItem = currentItem ? currentItem.name : 'No item selected'

  const descriptionLabel = useMemo(() => {
    const reason = currentItem?.reason
    const defaultKey = 'dispute:section75DisputeModal:disputeDetails:descriptionLabel'

    if (reason) {
      const reasonKey = `${defaultKey}.${reason}`
      const translated = t(reasonKey, { defaultValue: '' })
      if (translated) {
        return translated
      }
    }

    return t(defaultKey)
  }, [currentItem?.reason, t])

  // Set current item index to first item if not set
  React.useEffect(() => {
    if (currentItemIndex === null && selectedItems.length > 0) {
      setCurrentItemIndex(0)
    }
  }, [currentItemIndex, selectedItems, setCurrentItemIndex])

  // Pre-fill current item with previous item's values if not already set
  useEffect(() => {
    if (currentItemIndex !== null && currentItemIndex > 0) {
      setSelectedItems((prev) => {
        const current = prev[currentItemIndex]
        const previous = prev[currentItemIndex - 1]
        if (!current || !previous) return prev

        const needsReasonFill = !current.reason && previous.reason
        const needsDescriptionFill = !current.description && previous.description
        if (!needsReasonFill && !needsDescriptionFill) return prev

        const updated = [...prev]
        updated[currentItemIndex] = {
          ...current,
          reason: current.reason || previous.reason,
          description: current.description || previous.description,
        }
        return updated
      })
    }
  }, [currentItemIndex, setSelectedItems])

  const expectedDeliveryDateInvalid = useMemo<boolean>(() => {
    return expectedDeliveryDate !== null && isDateInvalid(expectedDeliveryDate, order)
  }, [expectedDeliveryDate, order])

  const dateErrorMsg = t('dispute:contactMerchantInfo.error.invalidDate')

  const nextBtnDisabled = useMemo<boolean>(() => {
    if (expectedDeliveryDateInvalid || expectedDeliveryDate === null) {
      return true
    }
    if (!currentItem?.reason) {
      return true
    }
    if (!currentItem?.description?.trim()) {
      return true
    }
    return false
  }, [expectedDeliveryDateInvalid, expectedDeliveryDate, currentItem?.reason, currentItem?.description])

  return (
    <>
      <Modal.Header divider={true}>
        <div className='absolute z-10 pt-1'>
          <Button.Link size='Small' onClick={backHandler}>
            <Icon.CaretLeft size='Small' />
          </Button.Link>
        </div>
        <div className='text-center'>
          <Heading size='L'>{t('dispute:section75DisputeModal:disputeDetails:heading')}</Heading>
          {selectedItems.length > 1 && currentItemIndex !== null && (
            <Text size='S' className='mt-1 text-gray-600'>
              {t('dispute:section75DisputeModal:disputeDetails:itemCount', { current: currentItemIndex + 1, total: selectedItems.length })}
            </Text>
          )}
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className='space-y-6'>
          {/* Selected Item Display */}
          <div>
            <Heading size='L'>{selectedItem}</Heading>
          </div>

          {/* Reason for Dispute Dropdown */}
          <div>
            <Dropdown
              label={t('dispute:section75DisputeModal:disputeDetails:disputeReasonLabel')}
              size='Large'
              onSelect={reasonChangeHandler}
              defaultValue={currentItem?.reason ?? ''}
            >
              {DISPUTE_REASON_OPTIONS.map((option) => (
                <Dropdown.MenuItem key={option.id} value={option.value}>
                  {option.label}
                </Dropdown.MenuItem>
              ))}
            </Dropdown>
          </div>

          {/* Expected Delivery Date */}
          <InputBirthday
            size='Large'
            onChange={expectedDeliveryDateChangeHandler}
            onBlur={expectedDeliveryDateBlurHandler}
            defaultValue={expectedDeliveryDate ?? undefined}
            locale={locale}
            minAge={Number.NEGATIVE_INFINITY}
            maxAge={Number.POSITIVE_INFINITY}
            error={expectedDeliveryDateInvalid}
            errorText={dateErrorMsg}
            name='expectedDeliveryDate'
            label={t('dispute:section75DisputeModal:disputeDetails:dateLabel')}
            hasDateFormatLabel={false}
          />

          {/* Description Text Area */}
          <div className={styles.textArea}>
            <Input.TextArea label={descriptionLabel} size='Large' value={currentItem?.description ?? ''} onChange={noteChangeHandler} rows={4} />
          </div>

          {/* Supporting Documents */}
          <div>
            <Text size={textSize} bold>
              {t('dispute:section75DisputeModal:disputeDetails:supportDocs')}
            </Text>
            <Text className='mt-1 text-gray-600'>{t('dispute:section75DisputeModal:disputeDetails.upload')}</Text>

            <div className='mt-3'>
              <Button.Ghost size='Regular' padding='Fluid' onClick={addFileButtonClickHandler}>
                {t('dispute:section75DisputeModal:disputeDetails:chooseFiles')}
              </Button.Ghost>
              <input type='file' className='hidden' multiple onChange={fileChangedHandler} accept='image/*,.pdf,.doc,.docx' ref={uploadRef} />
            </div>
            {/* Display uploaded files */}
            {files.length > 0 && (
              <div className='mt-3'>
                <Text className='text-gray-600'>{t('dispute:section75DisputeModal:disputeDetails:uploaded')}</Text>
                <ul className='mt-1'>
                  {files.map((file, index) => (
                    <li key={index} className='text-sm text-gray-700'>
                      <Text className='text-gray-600'>{file.name}</Text>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className='mt-6'>
          <Button padding='Fluid' size='Large' onClick={handleNext} disabled={nextBtnDisabled}>
            {t(
              `dispute:section75DisputeModal:disputeDetails:${currentItemIndex !== null && currentItemIndex < selectedItems.length - 1 ? 'next_item' : 'next'}`
            )}
          </Button>
        </div>
      </Modal.Content>
    </>
  )
}
